import { selectWarehouseAllStates } from 'containers/Warehouses/warehousesSlice'
import CheckingLocationPopup from 'components/popups/CheckingLocationPopup'
import { GATEWAY_STATUS, GATE_STATUS, STATUS } from 'utils/constants'
import { ReactComponent as MagIcon } from 'assets/magDetails.svg'
import { ReactComponent as HelpIcon } from 'assets/helpBig.svg'
import { ReactComponent as GateIcon } from 'assets/gate.svg'
import { ReactComponent as DoorIcon } from 'assets/door.svg'
import BigCircleBtn from 'components/buttons/BigCircleBtn'
import { selectConfig, selectLightTheme } from 'appSlice'
import Gateway from 'containers/entrance/core/Gateway'
import CircleBtn from 'components/buttons/CircleBtn'
import useTranslation from 'hooks/useTranslation'
import Gate from 'containers/entrance/core/Gate'
import usePopupState from 'hooks/usePopupState'
import { useHistory } from 'react-router-dom'
import LoginFormPopup from './LoginFormPopup'
import MagNumber from 'components/MagNumber'
import Warehouse from 'components/Warehouse'
import useData from '../core/hooks/useData'
import { useReduxSelector } from 'store'
import { POST } from 'rest/request'
import { useState } from 'react'
import { api } from 'rest/urls'
import '../style.scss'

export interface LoginForm {
  phone: string
  pin: string
}

type Type = 'gate' | 'gateway' | 'warehouse'

const Shared = () => {
  const config = useReduxSelector(selectConfig)
  const [type, setType] = useState<Type>('gate')
  const lightTheme = useReduxSelector(selectLightTheme)
  const states = useReduxSelector(selectWarehouseAllStates)
  const [loginForm, setLoginForm] = useState<LoginForm>({
    phone: '',
    pin: ''
  })

  const { isLoading, data } = useData()
  const [isSending, setSending] = useState(false)
  const { popup, openPopup } = usePopupState<{ type: 'loginForm' | 'checkingGateLocation', props: { type: Type } }>()

  const { push, goBack } = useHistory()
  const { t } = useTranslation('entrance')

  const action = async <T extends Type>(_type: T, form = loginForm) => {
    if (!data || isSending) return
    try {
      const additionalBody = data.needPin ? form : undefined
      const action = data?.actions?.[_type]?.open
      if (!action) throw new Error('No action')
      if (action.type === 'request' && action.method === 'POST') {
        setSending(true)
        const body = additionalBody
          ? { ...action.payload, ...additionalBody }
          : action.payload
        const response = await POST(`${api}${action.url}`, { body })
        setSending(false)
        if (response?.result === 'ok') {
          if (popup) {
            setLoginForm(form)
            goBack()
          }
        } else {
          setLoginForm({ phone: '', pin: '' })
          throw new Error('Wrong response')
        }

      } else {
        throw new Error('No supported action')
      }
    } catch (error) {
      setSending(false)
      console.error(error)
    }
  }

  const open = <T extends Type>(_type: T, enabled?: boolean) => {
    if (isSending) return
    if ((config?.warehouseLocationCheckEnabled && data?.placeGps) ? !enabled : false) {
      openPopup({ type: 'checkingGateLocation', props: { type: _type } })
    } else if (data?.needPin && (!loginForm.phone || !loginForm.pin)) {
      openPopup({ type: 'loginForm', props: { type: _type } }, enabled)
    } else {
      action(_type)
    }
  }

  const help = <CircleBtn
    text={t('help')}
    Icon={HelpIcon}
    action={() => {
      push('/help')
    }}
  />

  const magNumber = data?.number
    ? <MagNumber number={data.number} />
    : null

  return (
    <>
      {popup?.type === 'checkingGateLocation' && <CheckingLocationPopup
        targetPosition={(Array.isArray(data?.placeGps) && data?.placeGps.length === 2) ? {
          lat: Number(data?.placeGps[0]),
          lng: Number(data?.placeGps[1])
        } : undefined}
        callback={(success) => {
          if (success) {
            if (!data?.needPin) goBack()
            open(popup.props.type, true)
          } else {
            goBack()
          }
        }}
      />}
      {popup?.type === 'loginForm' && <LoginFormPopup
        isSending={isSending}
        loginForm={loginForm}
        login={form => {
          action(popup.props.type, form)
        }}
      />}

      {(() => {
        switch (type) {
          case 'gate': {
            const socketStatus = data?.gateId && data?.placeId && states
              ? states.places?.[data.placeId]?.gates?.[data.gateId]
              : undefined
            const status = socketStatus || data?.states.gate || 'gate_closed'

            return (
              <div className='entrance warehouse --full'>
                {magNumber}
                <div className={`entrance__main center${lightTheme ? '' : ' --shadow'}`}>
                  <div className='entrance__main__label --top'>{data?.address}</div>
                  <Gate status={status} />
                </div>

                <div className='entrance__menu menu__main'>
                  <div className='menu__buttons --top'>
                    <CircleBtn
                      disabled={!data?.actions?.gateway}
                      text={t('openGateway')}
                      Icon={DoorIcon}
                      action={() => {
                        setType('gateway')
                      }} />

                    <div className='buttons__col center'>
                      <BigCircleBtn
                        disabled={isSending || isLoading || !data?.actions?.gate?.open}
                        text={t(status === GATE_STATUS.opening ? 'opening'
                          : status === GATE_STATUS.closing ? 'closing'
                            : status === GATE_STATUS.opened ? 'driveThroughGate'
                              : 'open')}
                        status={status.includes('ing') ? STATUS.pending : STATUS.idle}
                        action={() => {
                          if (/* (status === GATE_STATUS.closed || status === GATE_STATUS.closing) && */data?.actions?.gate?.open) {
                            open(type)
                          }
                        }}
                      />
                      {help}
                    </div>

                    <CircleBtn
                      disabled={!data?.actions?.warehouse}
                      text={t('openMag')}
                      Icon={MagIcon}
                      action={() => {
                        setType('warehouse')
                      }} />
                  </div>
                </div>
              </div>
            )
          }

          case 'gateway': {
            const socketStatus = data?.gatewayId && data?.placeId && states
              ? states.places?.[data.placeId]?.gateways?.[data.gatewayId]
              : undefined
            const status = socketStatus || data?.states.gateway || 'gateway_closed'

            return (
              <div className='entrance warehouse --full'>
                {magNumber}
                <div className={`entrance__main center${lightTheme ? '' : ' --shadow'}`}>
                  <div className='entrance__main__label --top'>{data?.address}</div>
                  <Gateway status={status} />
                </div>

                <div className='entrance__menu menu__main'>
                  <div className='menu__buttons --top'>
                    <CircleBtn
                      disabled={!data?.actions?.gate}
                      text={t('openGate')}
                      Icon={GateIcon}
                      action={() => {
                        setType('gate')
                      }} />

                    <div className='buttons__col center'>
                      <BigCircleBtn
                        disabled={isSending || isLoading || !data?.actions?.gateway?.open}
                        text={t(status === GATEWAY_STATUS.closed ? 'open' : 'enterThroughGate')}
                        status={status === GATEWAY_STATUS.opened ? STATUS.succeed : STATUS.idle}
                        action={() => {
                          if (/* status === GATEWAY_STATUS.closed &&  */data?.actions?.gateway?.open) {
                            open(type)
                          }
                        }} />
                      {help}
                    </div>

                    <CircleBtn
                      disabled={!data?.actions?.warehouse}
                      text={t('openMag')}
                      Icon={MagIcon}
                      action={() => {
                        setType('warehouse')
                      }} />
                  </div>
                </div>
              </div>
            )
          }

          case 'warehouse': {
            const socketStatus = data?.id && states ? states.warehouses[data.id] : undefined
            const door = socketStatus?.door || data?.states.door || 'closed'
            const lock = socketStatus?.lock || data?.states.lock || 'locked'

            return (
              <div className='entrance warehouse --full'>
                {magNumber}
                <div className={`entrance__main center${lightTheme ? '' : ' --shadow'}`}>
                  <div className='entrance__main__label --top'>{data?.address}</div>
                  <Warehouse
                    door={door}
                    lock={lock}
                  />
                </div>

                <div className='entrance__menu menu__main'>
                  <div className='menu__buttons --top'>
                    <CircleBtn
                      disabled={!data?.actions?.gate}
                      text={t('openGate')}
                      Icon={GateIcon}
                      action={() => {
                        setType('gate')
                      }} />

                    <div className='buttons__col center'>
                      <BigCircleBtn
                        disabled={isSending || isLoading || !data?.actions?.warehouse?.open}
                        text={door === 'opened'
                          ? t('doorOpen')
                          : lock === 'unlocked'
                            ? t('pullTheDoor')
                            : t('open')}
                        status={door === 'opened'
                          ? STATUS.succeed
                          : lock === 'unlocked'
                            ? STATUS.pending
                            : STATUS.idle}
                        action={() => {
                          if (lock === 'locked' && data?.actions?.warehouse?.open) {
                            open(type)
                          }
                        }}
                      />
                      {help}
                    </div>

                    <CircleBtn
                      disabled={!data?.actions?.gateway}
                      text={t('openGateway')}
                      Icon={DoorIcon}
                      action={() => {
                        setType('gateway')
                      }} />
                  </div>
                </div>
              </div>
            )
          }
        }
      })()}
    </>
  )
}

export default Shared